import { useId } from "react";

import { useTheme } from "../../theme";

type SiteLogoMarkProps = {
  className?: string;
  title?: string;
};

const LOGO_PALETTE = {
  dark: {
    fieldInner: "#163028",
    fieldOuter: "#0a100e",
    tileStroke: "#163028",
    tileStrokeOpacity: 0,
    omega: "#8ee4af",
    omegaMid: "#5cdb95",
    omegaDeep: "#379683",
    omegaShadow: "#05386b",
    omegaShadowOpacity: 0.5,
    contour: "#8ee4af",
    contourSoft: "#5cdb95",
  },
  light: {
    fieldInner: "#e7f3d8",
    fieldOuter: "#f7fbf0",
    tileStroke: "#379683",
    tileStrokeOpacity: 0.5,
    omega: "#2a7d6c",
    omegaMid: "#379683",
    omegaDeep: "#05386b",
    omegaShadow: "#05386b",
    omegaShadowOpacity: 0.22,
    contour: "#24685a",
    contourSoft: "#379683",
  },
} as const;

export function SiteLogoMark({
  className = "h-8 w-8",
  title = "whatIsUp",
}: SiteLogoMarkProps) {
  const { theme } = useTheme();
  const palette = LOGO_PALETTE[theme];
  const id = useId().replace(/:/g, "");
  const fieldId = `logo-field-${id}`;
  const omegaStrokeId = `logo-omega-stroke-${id}`;
  const contourFillId = `logo-contour-fill-${id}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      role="img"
      aria-label={title}
      className={className}
    >
      <defs>
        <radialGradient id={fieldId} cx="50%" cy="58%" r="58%">
          <stop offset="0%" stopColor={palette.fieldInner} />
          <stop offset="100%" stopColor={palette.fieldOuter} />
        </radialGradient>
        <linearGradient id={omegaStrokeId} x1="82%" y1="92%" x2="18%" y2="8%">
          <stop offset="0%" stopColor={palette.omega} />
          <stop offset="45%" stopColor={palette.omegaMid} />
          <stop offset="100%" stopColor={palette.omegaDeep} />
        </linearGradient>
        <radialGradient id={contourFillId} cx="50%" cy="22%" r="70%">
          <stop offset="0%" stopColor={palette.contour} stopOpacity="0.5" />
          <stop offset="55%" stopColor={palette.contourSoft} stopOpacity="0.28" />
          <stop offset="100%" stopColor={palette.contourSoft} stopOpacity="0.12" />
        </radialGradient>
      </defs>

      <rect
        width="32"
        height="32"
        rx="7"
        fill={`url(#${fieldId})`}
        stroke={palette.tileStroke}
        strokeWidth="0.7"
        strokeOpacity={palette.tileStrokeOpacity}
      />

      <g transform="rotate(180 16 16)">
        <path
          d="M 7.4 24.05
             C 10.1 24.45 9.75 22.15 9.45 19.6
             C 9.05 16.2 7.55 12.8 7.25 10.2
             C 7.0 7.9 10.1 6.7 12.75 6.18
             C 14.05 6.02 15.1 7.65 16 7.75
             C 16.9 7.65 17.95 6.02 19.25 6.18
             C 21.9 6.7 24.95 7.9 24.75 10.2
             C 24.45 12.8 22.95 16.2 22.55 19.6
             C 22.25 22.15 21.9 24.45 24.6 24.05"
          fill="none"
          stroke={palette.omegaShadow}
          strokeWidth="3.85"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={palette.omegaShadowOpacity}
        />
        <path
          d="M 7.4 24.05
             C 10.1 24.45 9.75 22.15 9.45 19.6
             C 9.05 16.2 7.55 12.8 7.25 10.2
             C 7.0 7.9 10.1 6.7 12.75 6.18
             C 14.05 6.02 15.1 7.65 16 7.75
             C 16.9 7.65 17.95 6.02 19.25 6.18
             C 21.9 6.7 24.95 7.9 24.75 10.2
             C 24.45 12.8 22.95 16.2 22.55 19.6
             C 22.25 22.15 21.9 24.45 24.6 24.05"
          fill="none"
          stroke={`url(#${omegaStrokeId})`}
          strokeWidth="2.55"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      <path
        d="M 11.05 19.55
           C 11.05 22.15 20.95 22.15 20.95 19.55
           C 20.8 15.7 18.15 13.2 16 12.95
           C 13.85 13.2 11.2 15.7 11.05 19.55 Z"
        fill={`url(#${contourFillId})`}
      />
      {[
        {
          cy: 19.55,
          rx: 5.05,
          ry: 2.02,
          fillOpacity: theme === "dark" ? 0.22 : 0.16,
          strokeOpacity: theme === "dark" ? 0.88 : 0.62,
          strokeWidth: theme === "dark" ? 0.82 : 0.72,
        },
        {
          cy: 16.15,
          rx: 3.88,
          ry: 1.52,
          fillOpacity: theme === "dark" ? 0.2 : 0.14,
          strokeOpacity: theme === "dark" ? 0.92 : 0.74,
          strokeWidth: theme === "dark" ? 0.76 : 0.68,
        },
        {
          cy: 14.72,
          rx: 2.4,
          ry: 0.98,
          fillOpacity: theme === "dark" ? 0.24 : 0.16,
          strokeOpacity: theme === "dark" ? 0.96 : 0.86,
          strokeWidth: theme === "dark" ? 0.72 : 0.64,
        },
        {
          cy: 13.88,
          rx: 1.14,
          ry: 0.46,
          fillOpacity: theme === "dark" ? 0.55 : 0.5,
          strokeOpacity: 1,
          strokeWidth: theme === "dark" ? 0.68 : 0.6,
        },
      ].map((ring) => (
        <ellipse
          key={ring.cy}
          cx="16"
          cy={ring.cy}
          rx={ring.rx}
          ry={ring.ry}
          fill={palette.contourSoft}
          fillOpacity={ring.fillOpacity}
          stroke={palette.contour}
          strokeWidth={ring.strokeWidth}
          strokeOpacity={ring.strokeOpacity}
        />
      ))}
    </svg>
  );
}
